/**
 * Systems list hook using React Relay.
 *
 * Server-side search (name ilike) and cursor pagination over listSystems.
 * The query is token-scoped: the API only returns systems the current admin
 * can access, so no explicit access filter is needed (the legacy REST
 * `adminsystems` endpoint semantics).
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useSystemsRelayDirectQuery as UseSystemsRelayDirectQuery,
  useSystemsRelayDirectQuery$variables,
  SystemFilterInput,
  SystemSortInput,
} from './__generated__/useSystemsRelayDirectQuery.graphql';

const SystemsListDirectQuery = graphql`
  query useSystemsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: SystemFilterInput
    $sort: [SystemSortInput!]
  ) {
    listSystems(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          status
          organizationId
          organization {
            id
            name
          }
        }
      }
    }
  }
`;

export type { SystemFilterInput, SystemSortInput };

export interface SystemGraphQL {
  id: string;
  oldId: string | null;
  name: string | null;
  status: string | null;
  organizationId: string | null;
  organization: {
    id: string;
    name: string;
  } | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface SystemQueryFilter {
  search?: string;
  organizationIds?: string[];
}

function buildRelayFilter(filter?: SystemQueryFilter): SystemFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: SystemFilterInput = {};

  if (filter.search) {
    relayFilter.name = { ilike: `%${filter.search}%` };
  }
  if (filter.organizationIds?.length) {
    relayFilter.organizationId = { in: filter.organizationIds };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

/**
 * Hook that provides React Query-like interface for the systems list.
 *
 * Does NOT use Suspense - provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, count, hasMore, endCursor, isLoading } = useSystemsRelayQuery(
 *   { search: 'bike', organizationIds: ['...'] },
 *   { limit: 25, after: afterCursor }
 * );
 */
export function useSystemsRelayQuery(
  filter?: SystemQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: SystemSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<SystemGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useSystemsRelayDirectQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseSystemsRelayDirectQuery>(
          relayEnvironment,
          SystemsListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listSystems?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as SystemGraphQL[]);
          setCount(result.listSystems?.count ?? undefined);
          setHasMore(result.listSystems?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listSystems?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch systems'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
